import {
  ArrowDown,
  BookOpen,
  Building2,
  Beaker,
  Brain,
  Database,
  FileSpreadsheet,
  FileText,
  FlaskConical,
  GitBranch,
  GraduationCap,
  Layers,
  Network,
  NotebookPen,
  Scale,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  Workflow,
} from "lucide-react";

const INPUTS = [
  { icon: BookOpen, label: "Literature Databases", detail: "PubMed · arXiv · Web of Science · Scopus" },
  { icon: Scale, label: "Patent Databases", detail: "USPTO · EPO · WIPO · Google Patents" },
  { icon: FileText, label: "Internal Documents", detail: "Design docs · SOPs · reports · slide decks" },
  { icon: NotebookPen, label: "Lab Notes", detail: "Notebook entries · protocols · datasets" },
];

const OUTPUTS = [
  { icon: TrendingUp, label: "Evidence Ranking", detail: "Credibility + recency + reuse scored 0-100." },
  { icon: GitBranch, label: "Cross-Domain Bridges", detail: "Structural links between unrelated fields." },
  { icon: Sparkles, label: "Hypothesis Generation", detail: "Grounded, testable — every claim cited." },
  { icon: Workflow, label: "Research Roadmaps", detail: "Milestone tree with dependencies + risks." },
  { icon: Layers, label: "TRL Assessment", detail: "Technology Readiness Level per component." },
  { icon: FileSpreadsheet, label: "Executive Summaries", detail: "Auto-generated PDF, ready to circulate." },
  { icon: Network, label: "Structural Diagrams", detail: "SVG graphs of the reasoning trail." },
];

const CONSUMERS = [
  { icon: FlaskConical, label: "Lab Scientists", detail: "Faster literature triage, cleaner hypotheses." },
  { icon: Beaker, label: "Engineering Teams", detail: "TRL-anchored feasibility calls before commit." },
  { icon: Database, label: "Knowledge Platforms", detail: "Embed synthesis into existing search UIs." },
  { icon: Brain, label: "Enterprise AI", detail: "Grounded prompts + verified retrieval feeds." },
  { icon: GraduationCap, label: "Grant Writers", detail: "Evidence-backed narratives + prior-art proof." },
  { icon: Users, label: "Decision Makers", detail: "One-page defensible summaries under time pressure." },
];

function Cell({ icon: Icon, label, detail, accent }) {
  return (
    <div
      className={
        "border bg-[#0A0A0A] p-3 flex gap-3 items-start hover:bg-[#0E0E0E] transition-colors " +
        accent
      }
    >
      <Icon size={16} className="shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[12px] text-white leading-tight">{label}</div>
        <div className="text-[11px] text-zinc-500 leading-snug">{detail}</div>
      </div>
    </div>
  );
}

export default function UpstreamPositioningDiagram() {
  return (
    <section
      data-testid="landing-upstream-diagram"
      className="max-w-7xl mx-auto px-4 sm:px-6 py-14"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-purple-300">
        HOW_MINDSTEIN_FITS
      </div>
      <h2 className="mt-2 text-2xl sm:text-3xl font-mono tracking-tight text-white max-w-3xl leading-tight">
        MindStein sits <span className="text-amber-300">above</span> your existing
        stack — and makes it{" "}
        <span className="text-emerald-300">smarter</span>.
      </h2>
      <p className="mt-3 text-sm text-zinc-400 leading-relaxed max-w-3xl">
        Not another AI search box, not another paper summariser, not a
        competitor to your lab notebook. MindStein is an{" "}
        <span className="text-white">upstream synthesis layer</span> — it
        ingests from every research source you already trust, produces
        structured, verifiable outputs, and plugs those outputs into the tools
        and teams that need them.
      </p>

      {/* diagram */}
      <div className="mt-8 border border-[#27272A] bg-[#050505] p-4 sm:p-6 space-y-5">
        {/* INPUTS */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-sky-300 mb-3 flex items-center gap-2">
            <ArrowDown size={12} /> INGESTS · what MindStein reads
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {INPUTS.map((i) => (
              <Cell
                key={i.label}
                {...i}
                accent="border-sky-400/30 text-sky-200"
              />
            ))}
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowDown size={20} className="text-purple-300 animate-pulse" />
        </div>

        {/* MINDSTEIN CORE */}
        <div
          data-testid="landing-upstream-core"
          className="border-2 border-purple-400/50 bg-purple-500/[0.08] p-5 sm:p-6 text-center"
        >
          <div className="flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-purple-200">
            <ShieldCheck size={12} /> UPSTREAM SYNTHESIS LAYER
          </div>
          <div className="mt-2 text-2xl sm:text-3xl font-mono text-white tracking-tight">
            THE_MINDSTEIN
          </div>
          <div className="mt-2 text-[12px] text-purple-100 max-w-2xl mx-auto leading-relaxed">
            Grounded cross-domain synthesis · DOI-verified sources · MATH_CORE
            ranking · TRL scoring · SHA-256 + HMAC proof stamps. Everything
            below is generated, ranked, and cited by this single layer.
          </div>
          <div className="mt-3 flex flex-wrap justify-center gap-2 text-[10px] font-mono uppercase tracking-wider">
            <span className="border border-purple-400/40 bg-[#0A0A0A] text-purple-200 px-2 py-0.5">
              plug-in ready
            </span>
            <span className="border border-emerald-400/40 bg-[#0A0A0A] text-emerald-200 px-2 py-0.5">
              non-replacing
            </span>
            <span className="border border-amber-400/40 bg-[#0A0A0A] text-amber-200 px-2 py-0.5">
              verifiable
            </span>
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowDown size={20} className="text-emerald-300 animate-pulse" />
        </div>

        {/* OUTPUTS */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300 mb-3 flex items-center gap-2">
            <ArrowDown size={12} /> PRODUCES · what MindStein returns
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {OUTPUTS.map((o) => (
              <Cell
                key={o.label}
                {...o}
                accent="border-emerald-400/30 text-emerald-200"
              />
            ))}
          </div>
        </div>

        <div className="flex justify-center">
          <ArrowDown size={20} className="text-amber-300 animate-pulse" />
        </div>

        {/* CONSUMERS */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-amber-300 mb-3 flex items-center gap-2">
            <ArrowDown size={12} /> POWERS · who plugs MindStein in
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {CONSUMERS.map((c) => (
              <Cell
                key={c.label}
                {...c}
                accent="border-amber-400/30 text-amber-200"
              />
            ))}
          </div>
        </div>

        {/* Key message */}
        <div
          data-testid="landing-upstream-message"
          className="mt-2 border-l-4 border-emerald-400 bg-emerald-500/[0.05] px-4 py-3"
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300">
            KEY_POSITIONING
          </div>
          <p className="mt-1 text-[14px] text-white leading-snug">
            <span className="text-emerald-300">MindStein sits above existing platforms and makes them smarter.</span>{" "}
            <span className="text-zinc-300">
              It does not replace your literature search, your patent tool,
              your lab notebook, or your enterprise AI — it grounds them,
              cross-links them, and produces the structured, verifiable
              synthesis those tools were never built to generate.
            </span>
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-wider">
            <span className="inline-flex items-center gap-1 border border-emerald-400/40 bg-[#0A0A0A] text-emerald-200 px-2 py-0.5">
              <Building2 size={10} /> plugs into your stack
            </span>
            <span className="inline-flex items-center gap-1 border border-emerald-400/40 bg-[#0A0A0A] text-emerald-200 px-2 py-0.5">
              <ShieldCheck size={10} /> verifiable output
            </span>
            <span className="inline-flex items-center gap-1 border border-emerald-400/40 bg-[#0A0A0A] text-emerald-200 px-2 py-0.5">
              <Layers size={10} /> upstream · not a competitor
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
